import React, { useState, useEffect, useRef } from 'react';
import { formatDate } from '@/utils';
import { Button } from '@/components/ui/Button';
import { correspondences as correspondencesApi } from '@/api';

interface Attachment {
  _id: string;
  filename: string;
  s3Key?: string;
  s3Url?: string;
  extractedContent?: string;
  contentType?: string;
  size?: number;
}

interface SourceCorrespondence {
  _id: string;
  subject?: string;
  from?: string | { email?: string; _id?: string } | any;
  to?: string | { email?: string; _id?: string } | any;
  date?: string;
  body?: string;
  type?: string;
  closureType?: 'full' | 'partial';
  isPrimary?: boolean;
  attachments?: Attachment[];
}

interface SourceCorrespondencePanelProps {
  correspondences: SourceCorrespondence[];
  onViewAttachment: (attachment: Attachment) => void;
  canEdit?: boolean;
  onAddCorrespondence?: (correspondenceId: string, closureType: 'full' | 'partial') => Promise<void>;
  onUpdateClosureType?: (correspondenceId: string, closureType: 'full' | 'partial') => Promise<void>;
}

export const SourceCorrespondencePanel: React.FC<SourceCorrespondencePanelProps> = ({
  correspondences,
  onViewAttachment,
  canEdit = false,
  onAddCorrespondence,
  onUpdateClosureType
}) => {
  const [expandedCorrespondences, setExpandedCorrespondences] = useState<Set<string>>(new Set());
  const [expandedAttachments, setExpandedAttachments] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedCorrId, setSelectedCorrId] = useState<string | null>(null);
  const [newClosureType, setNewClosureType] = useState<'full' | 'partial'>('full');
  const [adding, setAdding] = useState(false);
  const [updatingClosure, setUpdatingClosure] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search
  useEffect(() => {
    if (!showAddModal) return;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await correspondencesApi.list({
          search: searchTerm,
          limit: 15,
          correspondenceStatus: 'open,partially_closed'
        });
        const data = (res as any)?.data || (res as any)?.correspondences || [];
        // Filter out already-added correspondences
        const existingIds = new Set(correspondences.map(c => c._id));
        setSearchResults(data.filter((c: any) => !existingIds.has(c._id)));
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchTerm, showAddModal, correspondences]);

  const handleAdd = async () => {
    if (!selectedCorrId || !onAddCorrespondence) return;
    setAdding(true);
    try {
      await onAddCorrespondence(selectedCorrId, newClosureType);
      setShowAddModal(false);
      setSearchTerm('');
      setSelectedCorrId(null);
      setNewClosureType('full');
      setSearchResults([]);
    } finally {
      setAdding(false);
    }
  };

  const handleToggleClosure = async (corrId: string, current: 'full' | 'partial') => {
    if (!onUpdateClosureType) return;
    const next = current === 'full' ? 'partial' : 'full';
    setUpdatingClosure(corrId);
    try {
      await onUpdateClosureType(corrId, next);
    } finally {
      setUpdatingClosure(null);
    }
  };

  // Helper to extract email string from from/to fields
  const getEmailString = (field: any): string | undefined => {
    if (!field) return undefined;
    if (typeof field === 'string') return field;
    if (typeof field === 'object' && field.email) return field.email;
    if (Array.isArray(field) && field.length > 0) {
      return field.map(f => typeof f === 'string' ? f : f?.email).filter(Boolean).join(', ');
    }
    return undefined;
  };

  const toggleCorrespondence = (id: string) => {
    const newExpanded = new Set(expandedCorrespondences);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCorrespondences(newExpanded);
  };

  const toggleAttachment = (id: string) => {
    const newExpanded = new Set(expandedAttachments);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedAttachments(newExpanded);
  };

  const ClosureBadge: React.FC<{ closureType: 'full' | 'partial'; corrId: string }> = ({ closureType, corrId }) => {
    const isFull = closureType === 'full';
    const isUpdating = updatingClosure === corrId;

    if (!canEdit || !onUpdateClosureType) {
      // Read-only badge
      return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
          isFull
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
        }`}>
          {isFull ? 'Close' : 'Partial'}
        </span>
      );
    }

    return (
      <button
        onClick={(e) => { e.stopPropagation(); handleToggleClosure(corrId, closureType); }}
        disabled={isUpdating}
        title={`Click to change to ${isFull ? 'partial close' : 'full close'}`}
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors ${
          isFull
            ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50'
            : 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50'
        } ${isUpdating ? 'opacity-50' : ''}`}
      >
        {isUpdating ? '...' : isFull ? 'Close' : 'Partial'}
      </button>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Source Correspondence ({correspondences.length})
        </h2>
        {canEdit && onAddCorrespondence && (
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-300 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 transition-colors"
          >
            <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add
          </button>
        )}
      </div>

      {correspondences.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 px-4">
          <p className="text-sm">No source correspondences</p>
          {canEdit && onAddCorrespondence && (
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              + Add a correspondence
            </button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {correspondences.map((correspondence) => {
            const isExpanded = expandedCorrespondences.has(correspondence._id);

            return (
              <div key={correspondence._id} className="p-4">
                {/* Correspondence Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => toggleCorrespondence(correspondence._id)}
                      className="text-left w-full hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded px-2 py-1 -mx-2 -my-1 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <svg
                          className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${
                            isExpanded ? 'transform rotate-90' : ''
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {correspondence.subject || 'No Subject'}
                        </h3>
                      </div>
                    </button>

                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      {/* Closure type badge */}
                      <ClosureBadge
                        closureType={correspondence.closureType || 'full'}
                        corrId={correspondence._id}
                      />
                      {correspondence.isPrimary && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          Primary
                        </span>
                      )}
                      {correspondence.type && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {correspondence.type}
                        </span>
                      )}
                    </div>

                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      {getEmailString(correspondence.from) && (
                        <p>
                          <span className="font-medium">From:</span> {getEmailString(correspondence.from)}
                        </p>
                      )}
                      {getEmailString(correspondence.to) && (
                        <p>
                          <span className="font-medium">To:</span> {getEmailString(correspondence.to)}
                        </p>
                      )}
                      {correspondence.date && (
                        <p>
                          <span className="font-medium">Date:</span>{' '}
                          {formatDate(correspondence.date)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="mt-3 space-y-3">
                    {/* Email Body */}
                    {correspondence.body && (
                      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Message Content
                        </h4>
                        <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap max-h-64 overflow-y-auto">
                          {correspondence.body}
                        </div>
                      </div>
                    )}

                    {/* Attachments */}
                    {correspondence.attachments && correspondence.attachments.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Attachments ({correspondence.attachments.length})
                        </h4>
                        <div className="space-y-2">
                          {correspondence.attachments.map((attachment) => {
                            const attachmentExpanded = expandedAttachments.has(attachment._id);

                            return (
                              <div
                                key={attachment._id}
                                className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <svg
                                      className="w-5 h-5 text-gray-400 flex-shrink-0"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {attachment.filename}
                                      </p>
                                      {attachment.size && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          {(attachment.size / 1024).toFixed(1)} KB
                                          {attachment.contentType && ` • ${attachment.contentType}`}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  <div className="flex items-center space-x-2">
                                    {attachment.extractedContent && (
                                      <button
                                        onClick={() => toggleAttachment(attachment._id)}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                                      >
                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                        </svg>
                                        {attachmentExpanded ? 'Hide OCR' : 'View OCR'}
                                      </button>
                                    )}
                                    {attachment.s3Url && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => onViewAttachment(attachment)}
                                      >
                                        Download
                                      </Button>
                                    )}
                                  </div>
                                </div>

                                {/* OCR Text */}
                                {attachmentExpanded && attachment.extractedContent && (
                                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                    <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Extracted Text (OCR)
                                    </h5>
                                    <div className="text-xs text-gray-900 dark:text-gray-100 whitespace-pre-wrap max-h-48 overflow-y-auto bg-white dark:bg-gray-800 rounded p-2 border border-gray-200 dark:border-gray-700">
                                      {attachment.extractedContent}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Correspondence Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Add Source Correspondence
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Search open correspondences to link to this response.
              </p>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by subject, document code, reference..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {searching && (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  Searching...
                </div>
              )}
              {!searching && searchTerm && searchResults.length === 0 && (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No open correspondences found
                </div>
              )}
              {!searching && !searchTerm && (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  Type to search open correspondences
                </div>
              )}
              {searchResults.map((corr: any) => {
                const isSelected = selectedCorrId === corr._id;
                const projectName = corr.projectId?.projectName || corr.projectRelationship?.projectName;
                const status = corr.correspondenceStatus || 'open';
                return (
                  <button
                    key={corr._id}
                    onClick={() => setSelectedCorrId(isSelected ? null : corr._id)}
                    className={`w-full text-left p-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    {/* Document code + direction badge row */}
                    <div className="flex items-center gap-2 mb-0.5">
                      {corr.documentCode && (
                        <span className="text-xs font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                          {corr.documentCode}
                        </span>
                      )}
                      {corr.direction && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none ${
                          corr.direction === 'inward'
                            ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                            : corr.direction === 'outward'
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {corr.direction === 'inward' ? 'IN' : corr.direction === 'outward' ? 'OUT' : 'INT'}
                        </span>
                      )}
                      {status === 'partially_closed' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          Partial
                        </span>
                      )}
                    </div>
                    {/* Subject */}
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {corr.subject || 'No Subject'}
                    </p>
                    {/* Details row: project, date */}
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {projectName && (
                        <span className="truncate max-w-[180px]" title={projectName}>
                          {projectName}
                        </span>
                      )}
                      {projectName && (corr.dateReceived || corr.createdAt) && <span>·</span>}
                      {(corr.dateReceived || corr.createdAt) && (
                        <span className="whitespace-nowrap">{formatDate(corr.dateReceived || corr.createdAt)}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Closure Type Selection + Actions */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  When response is sent, this correspondence will be:
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewClosureType('full')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                      newClosureType === 'full'
                        ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 dark:border-green-600'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    Closed
                  </button>
                  <button
                    onClick={() => setNewClosureType('partial')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                      newClosureType === 'partial'
                        ? 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-600'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    Partially Closed
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => { setShowAddModal(false); setSearchTerm(''); setSelectedCorrId(null); setSearchResults([]); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!selectedCorrId || adding}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {adding ? 'Adding...' : 'Add Correspondence'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
